import * as React from "react";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { TabStrip, TabStripTab } from "@progress/kendo-react-layout";
import Title from "./TabTitle";
import Nominal from "../../pages/Nominal/Nominal";
import BatchPostings from "../../pages/BatchPosting/index";
import NominalLedger from "../../pages/Nominal/NominalLedger";
import JournalPostings from "../../pages/JournalPosting/JournalPosting";
import TransactionPeriod from "../../pages/TransactionPeriod/TransactionPeriod";
import YearEnd from "../../pages/YearEnd/YearEnd";
import { useNavigate, useMatch } from "react-router-dom";
import { addTab, removeTab, useTabs, TAB_TYPES } from "slices/tabsSlice";
import CONSTANT from "CONSTANT/Matter/PostingTypes";
import VatReturn from "pages/VatReturn/VatSummary";
import BankRec from "pages/BankRec";
import { Tabs } from "components/Enums/TabEnums.ts";
import { useAllowedLiscence } from "hooks/useAllowedLiscence";
import AccessRevokedModal from "components/common/AccessRevokedModal";

const NominalTabs = React.memo((props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tabs, setTabs] = useState([]);
  const [selected, setSelected] = useState(-1);

  const { allowed } = useAllowedLiscence(Tabs.NOMINAL);

  const tabsData = useTabs(TAB_TYPES.NOMINALS);

  const findContent = (item) => {
    switch (item) {
      case "nominals":
        return "Nominals";
      case "journalposting":
        return "Journal Posting";
      case "bankpostings":
        return "Bank Postings";
      case "nominalledger":
        return "Nominal Ledger";
      case "bankrec":
        return "Bank Rec";
      case "vatreturn":
        return "VAT Return";
      case "transactionperiod":
        return "Transaction Periods";
      case "yearend":
        return "Year End";
      default:
        break;
    }
  };

  const urlDefiner = (e) => {
    if (params !== null) {
      let url = tabs[e]?.toLowerCase();
      if (url === undefined) {
        url = "";
      }
      navigate(`/nominal/${url}`);
    }
  };

  const params = useMatch("/nominal/:data");

  useEffect(() => {
    if (params !== null) {
      let data = params?.params?.data;
      const dataLower = data?.toLowerCase();
      if (!tabsData.includes(dataLower)) {
        dispatch(addTab("nominals", dataLower));
      } else {
        setSelected(tabsData.indexOf(dataLower));
      }
    }
  }, [params, tabsData]);

  useEffect(() => {
    if (tabsData) {
      setTabs(tabsData);
      const segment = params?.params?.data?.toLowerCase();
      const index = segment ? tabsData.indexOf(segment) : -1;
      setSelected(index === -1 ? 0 : index);
    }
  }, [tabsData, params?.params?.data]);

  const removeTabDispatcher = (tab) => {
    const closedIndex = tabs.findIndex((t) => findContent(t) === tab);
    if (closedIndex === -1) return;

    const closedTabKey = tabs[closedIndex];
    const dispatchRemove = () => {
      queueMicrotask(() => {
        dispatch(removeTab("nominals", closedTabKey));
      });
    };

    const newTabs = tabs.filter((t) => findContent(t) !== tab);
    if (!newTabs.length) {
      navigate("/");
      setSelected(0);
      dispatchRemove();
      return;
    }
    const newSelected =
      closedIndex === selected
        ? Math.min(selected, newTabs.length - 1)
        : closedIndex < selected
          ? selected - 1
          : selected;
    setSelected(newSelected);
    navigate(`/nominal/${newTabs[newSelected]?.toLowerCase() ?? ""}`);
    dispatchRemove();
  };

  const handleSelect = (e) => {
    setSelected(e.selected);
    urlDefiner(e.selected);
  };

  return allowed ? (
    <TabStrip
      selected={selected}
      onSelect={handleSelect}
      keepTabsMounted={true}
      className="navigationTabs h-100">
      {tabs?.map((item) => {
        return (
          item && (
            <TabStripTab
              title={
                <Title
                  content={findContent(item)}
                  onTabRemove={removeTabDispatcher}
                  selected={findContent(tabs.at(selected))}
                  count={tabs.length}
                />
              }
              key={item}
              contentClassName="h-100">
              <div style={{ height: "100%" }}>
                {item === "nominals" && <Nominal />}
                {item === "nominalledger" && <NominalLedger />}
                {item === "journalposting" && <JournalPostings />}
                {item === "bankpostings" && <BatchPostings type={CONSTANT.BNKP} />}
                {item === "vatreturn" && <VatReturn />}
                {item === "bankrec" && <BankRec />}
                {item === "transactionperiod" && <TransactionPeriod />}
                {item === "yearend" && <YearEnd />}
              </div>
            </TabStripTab>
          )
        );
      })}
    </TabStrip>
  ) : (
    <AccessRevokedModal removeTab={() => props.closeTab()} />
  );
});

export default NominalTabs;
